// src/pages/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { login, loginWithGoogle, selectPendingGoogleDob } from '../store/authSlice';
import { FaEye, FaEyeSlash, FaUser, FaLock, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import GoogleSignIn from '../components/common/GoogleSignIn';
import { userNeedsDob } from '../utils/userNeedsDob';

/* ── tiny shared helpers ──────────────────────────────────────────────────── */
const FieldLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
    {children}
  </label>
);

const FieldError = ({ children }) =>
  children ? <p className="mt-1.5 text-xs text-red-500 font-medium">{children}</p> : null;

/* ── component ────────────────────────────────────────────────────────────── */
const LoginPage = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, loading, error } = useSelector((s) => s.auth);
  const pendingGoogleDob = useSelector(selectPendingGoogleDob);

  const [formData,     setFormData]     = useState({ usernameOrEmail: '', password: '' });
  const [formErrors,   setFormErrors]   = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const { from = '/explore', videoIds, thumbnails, duration } = location.state || {};

  /* redirect when authenticated (wait only if Google sign-in still needs DOB) */
  useEffect(() => {
    if (!user || (pendingGoogleDob && userNeedsDob(user))) return;
    if (videoIds && thumbnails && duration) {
      navigate('/confirmation', { state: { videoIds, thumbnails, duration } });
    } else {
      navigate(from, { replace: true });
    }
  }, [user, pendingGoogleDob, navigate, from, videoIds, thumbnails, duration]);

  /* toast on redux error */
  useEffect(() => { if (error) toast.error(error); }, [error]);

  const validate = () => {
    const e = {};
    if (!formData.usernameOrEmail.trim()) e.usernameOrEmail = 'Username or email is required.';
    if (!formData.password)               e.password        = 'Password is required.';
    else if (formData.password.length < 6) e.password       = 'Password must be at least 6 characters.';
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (formErrors[name]) setFormErrors(p => ({ ...p, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await dispatch(login(formData)).unwrap();
      toast.success('Login successful!');
    } catch (err) {
      toast.error(err || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async (_userData, response) => {
    if (!response?.credential) {
      setGoogleError('Google sign-in did not return a credential. Please try again.');
      return;
    }

    setGoogleError(null);
    setIsGoogleSubmitting(true);
    try {
      await dispatch(loginWithGoogle(response.credential)).unwrap();
      toast.success('Signed in with Google!');
    } catch (err) {
      const message = typeof err === 'string' ? err : 'Google sign-in failed. Please try again.';
      setGoogleError(message);
      toast.error(message);
    } finally {
      setIsGoogleSubmitting(false);
    }
  };

  const handleGoogleError = (error) => {
    const message = typeof error === 'string' ? error : 'Google sign-in failed. Please try again.';
    setGoogleError(message);
  };

  const inputCls = (field) =>
    `block w-full pl-10 pr-3 py-3 rounded-xl border text-sm text-gray-900 placeholder-gray-400
     focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red transition-all duration-150
     ${formErrors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`;

  /* ── render ── */
  return (
    <>
      {/* Page heading */}
      <div className="mb-8">
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight mb-1">Welcome back</h1>
        <p className="text-sm text-gray-500">Sign in to your Akture account to continue.</p>
      </div>

      {googleClientId && (
        <>
          <GoogleSignIn
            clientId={googleClientId}
            onSignIn={handleGoogleSignIn}
            onError={handleGoogleError}
            showOneTap={false}
            buttonOptions={{
              theme: 'outline',
              size: 'large',
              text: 'signin_with',
              width: 400,
            }}
            className="w-full max-w-none"
            style={{ maxWidth: '100%' }}
          />
          {googleError && (
            <p className="mt-2 text-xs text-red-500 font-medium text-center">{googleError}</p>
          )}
          {isGoogleSubmitting && (
            <p className="mt-2 text-xs text-gray-500 text-center">Completing Google sign-in…</p>
          )}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-gray-200"/>
            <span className="text-xs text-gray-400 font-medium">or</span>
            <div className="flex-1 h-px bg-gray-200"/>
          </div>
        </>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-5">

        {/* Username / Email */}
        <div>
          <FieldLabel htmlFor="usernameOrEmail">Username or Email</FieldLabel>
          <div className="relative">
            <FaUser className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"/>
            <input
              id="usernameOrEmail" name="usernameOrEmail" type="text"
              autoComplete="username" value={formData.usernameOrEmail}
              onChange={handleChange} placeholder="johndoe or john@example.com"
              className={inputCls('usernameOrEmail')}
              aria-invalid={!!formErrors.usernameOrEmail} aria-describedby="ue-error"
              disabled={isSubmitting || isGoogleSubmitting || loading}
            />
          </div>
          <FieldError>{formErrors.usernameOrEmail}</FieldError>
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <Link to="/forgot-password"
              className="text-[11px] font-semibold text-brand-red hover:text-red-700 transition-colors">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <FaLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"/>
            <input
              id="password" name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password" value={formData.password}
              onChange={handleChange} placeholder="••••••••"
              className={`${inputCls('password')} pr-11`}
              aria-invalid={!!formErrors.password} aria-describedby="pw-error"
              disabled={isSubmitting || isGoogleSubmitting || loading}
            />
            <button type="button" onClick={() => setShowPassword(p => !p)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-0.5"
              aria-label={showPassword ? 'Hide password' : 'Show password'}>
              {showPassword ? <FaEyeSlash className="text-sm"/> : <FaEye className="text-sm"/>}
            </button>
          </div>
          <FieldError>{formErrors.password}</FieldError>
        </div>

        {/* Submit */}
        <button type="submit" disabled={isSubmitting || isGoogleSubmitting || loading}
          className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-brand-red text-white text-sm font-bold rounded-xl hover:bg-red-700 transition-colors shadow-sm shadow-brand-red/20 disabled:opacity-50 disabled:cursor-not-allowed mt-2">
          {(isSubmitting || loading) ? (
            <><FaSpinner className="animate-spin text-base"/> Signing in…</>
          ) : 'Sign in'}
        </button>
      </form>

      {!googleClientId && (
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-gray-200"/>
          <span className="text-xs text-gray-400 font-medium">or</span>
          <div className="flex-1 h-px bg-gray-200"/>
        </div>
      )}

      {/* Register link */}
      <p className="text-center text-sm text-gray-600">
        Don&rsquo;t have an account?{' '}
        <Link to="/register" state={{ from, videoIds, thumbnails, duration }}
          className="font-bold text-brand-red hover:text-red-700 transition-colors">
          Create one free
        </Link>
      </p>

      {/* Terms */}
      <p className="text-center text-[10px] text-gray-400 mt-6 leading-relaxed">
        By signing in you agree to our{' '}
        <Link to="/terms-of-service" className="hover:text-gray-600 underline underline-offset-2">Terms</Link>
        {' '}and{' '}
        <Link to="/privacy-policy" className="hover:text-gray-600 underline underline-offset-2">Privacy Policy</Link>.
      </p>
    </>
  );
};

export default LoginPage;
